import OrganisationStore from './organisation-store'
import filter from 'lodash/filter'
import find from 'lodash/find'
import findIndex from 'lodash/findIndex'
import sortBy from 'lodash/sortBy'

import Constants from 'common/constants'
import Utils from 'common/utils/utils'
import { getStore } from 'common/store'
import { projectService } from 'common/services/useProject'
import { environmentService } from 'common/services/useEnvironment'

import Dispatcher from 'common/dispatcher/dispatcher'
import BaseStore from './base/_store'
import data from 'common/data/base/_data'

const controller = {
  createEnv: ({
    cloneFeatureStatesAsync,
    cloneId,
    description,
    metadata,
    name,
    projectId,
  }) => {
    API.trackEvent(Constants.events.CREATE_ENVIRONMENT)
    store.saving()
    const req = cloneId
      ? data.post(`${Project.api}environments/${cloneId}/clone/`, {
          clone_feature_states_async: cloneFeatureStatesAsync,
          description,
          metadata: metadata || [],
          name,
        })
      : data.post(`${Project.api}environments/`, {
          description,
          metadata: metadata || [],
          name,
          project: projectId,
        })
    req
      .then((res) =>
        data
          .put(`${Project.api}environments/${res.api_key}/`, {
            description,
            metadata: metadata || [],
            name,
            project: projectId,
          })
          .then((res) =>
            data
              .post(
                `${Project.api}environments/${
                  res.api_key
                }/${Utils.getIdentitiesEndpoint()}/`,
                {
                  environment: res.api_key,
                  identifier: `${name.toLowerCase()}_user_123456`,
                },
              )
              .then(() => {
                store.savedEnv = res
                if (store.model && store.model.environments) {
                  store.model.environments = store.model.environments.concat([
                    res,
                  ])
                }
                store.saved()
                getStore().dispatch(
                  environmentService.util.invalidateTags(['Environment']),
                )
                AppActions.refreshOrganisation()
              }),
          ),
      )
      .catch((e) => {
        API.ajaxHandler(store, e)
      })
  },

  deleteEnv: (env) => {
    API.trackEvent(Constants.events.REMOVE_ENVIRONMENT)
    data.delete(`${Project.api}environments/${env.api_key}/`).then(() => {
      store.model.environments = filter(
        store.model.environments,
        (e) => e.id !== env.id,
      )
      getStore().dispatch(
        environmentService.util.invalidateTags(['Environment']),
      )
      store.trigger('removed', env)
      store.saved()
      AppActions.refreshOrganisation()
    })
  },

  editEnv: (env) => {
    API.trackEvent(Constants.events.EDIT_ENVIRONMENT)
    data
      .put(`${Project.api}environments/${env.api_key}/`, env)
      .then((res) => {
        const index = findIndex(store.model.environments, { id: env.id })
        store.model.environments[index] = res
        store.saved()
        getStore().dispatch(
          environmentService.util.invalidateTags(['Environment']),
        )
        AppActions.refreshOrganisation()
      })
      .catch((e) => {
        e.json()
          .then((result) => {
            if (result?.metadata?.[0]) {
              toast(result.metadata[0], 'danger')
            } else {
              toast('Error updating the environment', 'danger')
            }
          })
          .catch((e) => {
            API.ajaxHandler(store, e)
          })
      })
  },
  editProject: (project) => {
    store.saving()
    data.put(`${Project.api}projects/${project.id}/`, project).then((res) => {
      store.model = Object.assign(store.model, res)
      getStore().dispatch(projectService.util.invalidateTags(['Project']))
      AppActions.refreshOrganisation()
      store.saved()
    })
  },
  getProject: (id, cb, force) => {
    if (!id) {
      !force && AsyncStorage.removeItem('lastEnv')
      document.location.href = '/404'
      return
    }

    if (!force && store.model && store.model.environments && store.id === id) {
      return
    }

    store.loading()

    return Promise.all([
      data.get(`${Project.api}projects/${id}/`),
      data.get(`${Project.api}environments/?project=${id}`).catch(() => []),
    ])
      .then(([project, environments]) => {
        project.total_features = project.total_features || 0
        project.total_segments = project.total_segments || 0
        store.model = Object.assign(project, {
          environments: sortBy(environments.results, 'name'),
        })
        if (project.organisation !== OrganisationStore.id) {
          AppActions.selectOrganisation(project.organisation)
          AppActions.getOrganisation(project.organisation)
        }
        store.id = id
        store.loaded()
        if (cb) {
          cb()
        }
      })
      .catch(() => {
        AsyncStorage.removeItem('lastEnv')
        document.location.href = '/404?entity=project'
      })
  },
}

const store = Object.assign({}, BaseStore, {
  getEnvironment: (api_key) =>
    store.model && find(store.model.environments, { api_key }),
  getEnvironmentById: (id) =>
    store.model && find(store.model.environments, { id }),
  getEnvironmentIdFromKey: (api_key) => {
    const env = find(store.model.environments, { api_key })
    return env && env.id
  },
  getEnvironmentIdFromKeyAsync: async (projectId, apiKey) => {
    if (store.model && `${store.model.id}` === `${projectId}`) {
      return await Promise.resolve(store.getEnvironmentIdFromKey(apiKey))
    }
    return await controller.getProject(projectId, null, true).then(() => {
      return Promise.resolve(store.getEnvironmentIdFromKey(apiKey))
    })
  },
  getEnvs: () => store.model && store.model.environments,
  getIsVersioned: (api_key) => {
    const env = find(store.model.environments, { api_key })
    return env && env.use_v2_feature_versioning
  },
  getMaxFeaturesAllowed: () => {
    return store.model && store.model.max_features_allowed
  },
  getMaxSegmentOverridesAllowed: () => {
    return store.model && store.model.max_segment_overrides_allowed
  },
  getMaxSegmentsAllowed: () => {
    return store.model && store.model.max_segments_allowed
  },
  getStaleFlagsLimit: () => {
    return store.model && store.model.stale_flags_limit_days
  },
  getTotalFeatures: () => {
    return store.model && store.model.total_features
  },
  getTotalSegmentOverrides: () => {
    return store.model && store.model.environment.total_segment_overrides
  },
  getTotalSegments: () => {
    return store.model && store.model.total_segments
  },
  id: 'project',
  model: null,
})

store.dispatcherIndex = Dispatcher.register(store, (payload) => {
  const action = payload.action // this is our action from handleViewAction

  switch (action.actionType) {
    case Actions.GET_PROJECT:
      controller.getProject(parseInt(action.projectId))
      break
    case Actions.CREATE_ENV:
      controller.createEnv(action)
      break
    case Actions.EDIT_ENVIRONMENT:
      controller.editEnv(action.env)
      break
    case Actions.DELETE_ENVIRONMENT:
      controller.deleteEnv(action.env)
      break
    case Actions.EDIT_PROJECT:
      controller.editProject(action.id, action.project)
      break
    case Actions.LOGOUT:
      store.model = null
      break
    default:
  }
})
controller.store = store
export default controller.store
